import React from 'react';
import { useLocation } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../../hooks';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import { Organization, UpdateRequest } from '../../../models';
import { EntityState, organizationNameSchema } from 'adc-sirius-module-common';
import * as Yup from 'yup';
import i18n from '../../../utilities/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  organizationCapabilitiesQueryOptions,
  organizationQueryOptions,
  updateOrganizationMutateOptions,
} from '../../../queryOptions';
import { getErrorMessageOnEntityCreateUpdate } from '../../../utilities/crudHelpers';

type OrganizationHeaderProps = {
  organization: Organization;
};

type OrganizationFormValues = Organization & UpdateRequest;

/**
 * OrganizationHeader component displays the header information for a specific organization.
 * @param {Organization} organization - The organization object containing details to display.
 * @returns {React.JSX.Element}
 */
export default function OrganizationHeader({
  organization,
}: OrganizationHeaderProps) {
  const queryClient = useQueryClient();

  const capabilities = useQuery(
    organizationCapabilitiesQueryOptions({ key: organization.key }),
  );

  const location = useLocation();
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const updateBreadcrumb = (label) => {
    navigateWithBreadcrumbs(location.pathname, label, label, {
      replace: true,
    });
  };

  const mapFormValuesToOrganization = (
    values: Partial<OrganizationFormValues>,
  ): Organization => {
    const { newUpdateReason, ...updatedValues } = values;

    return {
      ...organization,
      ...updatedValues,
      updateReason: newUpdateReason as string,
    };
  };

  const mapOrganizationToFormValues = (
    source: Organization,
  ): OrganizationFormValues => ({
    ...source,
    newUpdateReason: '',
  });

  const formConfig: DynamicFormConfig<OrganizationFormValues> = {
    fields: [
      {
        name: 'name',
        label: i18n.t('Entity.name'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'description',
        label: i18n.t('Entity.description'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'region',
        label: i18n.t('Entity.region'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'type',
        label: i18n.t('Entity.type'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Study.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'createdBy',
        label: i18n.t('Entity.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Entity.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Entity.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Entity.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Entity.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      key: Yup.string().required(i18n.t('Form.genericRequired')),
      name: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .matches(
          organizationNameSchema.matcher,
          i18n.t('Form.allowedSpecialCharacters', {
            characters:
              organizationNameSchema.allowedSpecialCharacters.join(' '),
          }),
        )
        .test(
          'len',
          () =>
            i18n.t('Form.genericMaxLength', {
              count: organizationNameSchema.maxLength,
            }),
          (val) =>
            organizationNameSchema.maxLength && val
              ? val.length <= organizationNameSchema.maxLength
              : true,
        ),
      type: Yup.string().optional(),
      region: Yup.string().optional(),
      description: Yup.string().optional(),
      state: Yup.mixed()
        .oneOf(Object.values(EntityState))
        .required(i18n.t('Form.genericRequired')),
      createdBy: Yup.string().required(i18n.t('Form.genericRequired')),
      createdOn: Yup.number().required(i18n.t('Form.genericRequired')),
      updatedBy: Yup.string(),
      updatedOn: Yup.number(),
      updateReason: Yup.string(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
    },
    initialValues: mapOrganizationToFormValues(organization),
  };

  const updateOrganization = useMutation(updateOrganizationMutateOptions());

  const handleSubmit = async (values: Partial<OrganizationFormValues>) => {
    const organizationValues = mapFormValuesToOrganization(values);

    return updateOrganization
      .mutateAsync({
        organizationKey: organization.key,
        updatedOrganizationValues: organizationValues,
      })
      .then((updatedOrganization) => {
        const organizationQueryKey = organizationQueryOptions({
          key: updatedOrganization.key,
        }).queryKey;

        queryClient.setQueryData(organizationQueryKey, updatedOrganization);
        updateBreadcrumb(updatedOrganization.name);

        return mapOrganizationToFormValues(updatedOrganization);
      })
      .catch((err) => {
        throw new Error(
          getErrorMessageOnEntityCreateUpdate(err, 'Page_Organization'),
        );
      });
  };

  return (
    <VStack flexGrow={1} gap="$4" testID="organization-details">
      <Card>
        <DynamicForm
          id="organization-details"
          title={i18n.t('Page_Organization.title')}
          formConfig={formConfig}
          onSave={handleSubmit}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
          isReadonly={!capabilities.data?.canUpdateOrganization}
        />
      </Card>
    </VStack>
  );
}
